import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useCertificationsExpiring } from '@/hooks/useRPL';
import { AlertTriangle, Award, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface CertificationExpiryListProps {
  limit?: number;
  compact?: boolean;
}

export function CertificationExpiryList({ limit, compact = false }: CertificationExpiryListProps) {
  const { data: certifications, isLoading } = useCertificationsExpiring();

  const displayCerts = limit ? certifications?.slice(0, limit) : certifications;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  if (!displayCerts?.length) {
    return (
      <Card className="p-4 text-center">
        <Award className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">No hay certificaciones por vencer</p>
      </Card>
    );
  }

  const getUrgencyColor = (days: number) => {
    if (days <= 0) return 'bg-red-500/10 text-red-500 border-red-500';
    if (days <= 15) return 'bg-orange-500/10 text-orange-500 border-orange-500';
    if (days <= 30) return 'bg-yellow-500/10 text-yellow-500 border-yellow-500';
    return 'bg-blue-500/10 text-blue-500 border-blue-500';
  };

  if (compact) {
    return (
      <div className="space-y-2">
        {displayCerts.map((cert) => (
          <div
            key={cert.id}
            className={`flex items-center justify-between p-3 rounded-lg border ${getUrgencyColor(cert.days_until_expiry)}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              {cert.days_until_expiry <= 0 ? (
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              ) : (
                <Clock className="w-4 h-4 flex-shrink-0" />
              )}
              <div className="min-w-0">
                <p className="font-medium text-sm truncate">{cert.employee_name}</p>
                <p className="text-xs text-muted-foreground truncate">{cert.training_type_name}</p>
              </div>
            </div>
            <Badge variant="outline" className={getUrgencyColor(cert.days_until_expiry)}>
              {cert.days_until_expiry <= 0
                ? 'Vencido'
                : `${cert.days_until_expiry} dias`}
            </Badge>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {displayCerts.map((cert) => (
        <Card key={cert.id} className={`border ${getUrgencyColor(cert.days_until_expiry)}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Award className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">{cert.training_type_name}</span>
                </div>
                <p className="text-sm">{cert.employee_name}</p>
                {cert.session_title && (
                  <p className="text-xs text-muted-foreground">{cert.session_title}</p>
                )}
              </div>
              <div className="text-right">
                <Badge className={getUrgencyColor(cert.days_until_expiry)}>
                  {cert.days_until_expiry <= 0 ? (
                    <span className="flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Vencido
                    </span>
                  ) : (
                    `${cert.days_until_expiry} dias`
                  )}
                </Badge>
                <p className="text-xs text-muted-foreground mt-1">
                  {format(new Date(cert.certificate_expiry), 'dd/MM/yyyy', { locale: es })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
